import { db } from '../db/indexedDb';
import { Artwork } from '../components/Artwork';
import { NextIcon, PauseIcon, PlayIcon, PreviousIcon } from '../components/PlayerIcons';
import { formatDuration } from '../lib/audio';
import { usePlayer } from '../hooks/usePlayer';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Track } from '../types';

const EMPTY_TRACKS: Track[] = [];

export function NowPlayingPage() {
  const {
    currentTrack,
    currentTime,
    duration,
    isPlaying,
    repeatMode,
    shuffleEnabled,
    playPause,
    previous,
    next,
    seek,
    setRepeatMode,
    toggleShuffle,
    queue,
    currentIndex,
    setQueueOpen
  } = usePlayer();

  const upcomingQuery = useLiveQuery(async () => {
    const ids = queue.slice(currentIndex + 1, currentIndex + 6);
    const tracks = await Promise.all(ids.map((id) => db.tracks.get(id)));
    return tracks.filter((track): track is Track => Boolean(track));
  }, [queue, currentIndex], EMPTY_TRACKS);
  const upcoming = upcomingQuery ?? EMPTY_TRACKS;

  return (
    <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
      <div className="panel rounded-[2rem] p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-accent">Now Playing</p>
        <div className="mt-6 flex flex-col items-center text-center">
          <Artwork track={currentTrack} size="xl" className="max-w-full" />
          <h2 className="mt-8 font-display text-4xl font-bold">
            {currentTrack?.title ?? 'Queue something beautiful'}
          </h2>
          <p className="mt-2 text-foreground/70">
            {currentTrack ? `${currentTrack.artist} • ${currentTrack.album}` : 'Upload tracks or add songs to the queue.'}
          </p>
          <div className="mt-8 w-full">
            <label className="sr-only" htmlFor="timeline">
              Seek within current track
            </label>
            <input
              id="timeline"
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={(event) => seek(Number(event.target.value))}
              className="w-full accent-[hsl(var(--accent))]"
            />
            <div className="mt-2 flex justify-between text-sm text-foreground/60">
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => void previous()}
              className="icon-button"
              aria-label="Previous track"
            >
              <PreviousIcon />
            </button>
            <button
              type="button"
              onClick={playPause}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent text-black"
              aria-label={isPlaying ? 'Pause playback' : 'Play current track'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button
              type="button"
              onClick={() => void next()}
              className="icon-button"
              aria-label="Next track"
            >
              <NextIcon />
            </button>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={() => void toggleShuffle()} className={`pill ${shuffleEnabled ? 'bg-accent text-black' : ''}`}>
              Shuffle: {shuffleEnabled ? 'On' : 'Off'}
            </button>
            <button
              type="button"
              onClick={() =>
                void setRepeatMode(
                  repeatMode === 'off' ? 'all' : repeatMode === 'all' ? 'one' : 'off'
                )
              }
              className="pill"
            >
              Repeat: {repeatMode}
            </button>
            <button type="button" onClick={() => setQueueOpen(true)} className="pill">
              Open queue
            </button>
          </div>
        </div>
      </div>

      <div className="panel rounded-[2rem] p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-accent">Upcoming</p>
        <h3 className="mt-3 font-display text-2xl font-semibold">Next in line</h3>
        <ul className="mt-6 space-y-3">
          {upcoming.length ? (
            upcoming.map((track, index) => (
              <li key={track.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                <span className="pill">{currentIndex + index + 2}</span>
                <Artwork track={track} size="sm" />
                <div className="min-w-0">
                  <p className="truncate font-medium">{track.title}</p>
                  <p className="truncate text-sm text-foreground/65">
                    {track.artist} • {formatDuration(track.duration)}
                  </p>
                </div>
              </li>
            ))
          ) : (
            <li className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-foreground/70">
              Nothing is queued after this track yet.
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}
